<template>
  <shared-product-provide :product="product" :fromModule="fromModule" :toModule="toModule">
    <loader :loading="loadingAttributes || loadingProductTypes"/>

    <form-group :label="$t('brand.label')" :columns="3" :error="getError('brand', product.productId)" :required="true">
      <multiselect
        v-model="product.brand"
        :options="brands"
        selectLabel=""
        selectedLabel=""
        placeholder="Buscar Marca"
      />
    </form-group>

    <form-group :label="$t('departments.label')" :columns="3" :error="getError('department', product.productId)" :required="true">
      <multiselect
        v-model="department"
        :options="departments"
        selectLabel=""
        selectedLabel=""
        placeholder="Escolha"
        group-label="channel"
        group-values="departments"
        track-by="value"
        label="label"
      />
    </form-group>

    <form-group :label="$t('productTypes.label')" :columns="3" :error="getError('productType', product.productId)" v-if="productTypes.length" :required="true">
      <multiselect
        v-model="productType"
        :options="productTypes"
        selectLabel=""
        selectedLabel=""
        placeholder="Escolha"
      />
    </form-group>

    <div class="l-col-12@md" v-if="attributes.length">
      <div class="divider"><span>Atributos</span></div>
    </div>
    <form-group v-for="(attribute, index) in attributes" :key="attribute.code" :label="attribute.name" :columns="3" :error="getError(attribute.code, product.productId)" :required="attribute.required">
      <select v-model="attribute.value" class="form-select" :disabled="disabledInputs()" v-if="attribute.typeSelection === 'TIPO_SELECAO_LISTA'">
        <option value="undefined">{{ $t('choose.label') }}</option>
        <option v-for="option in attribute.values" :value="option">{{ option }}</option>
      </select>
      <multiselect v-model="attribute.value" :options="attribute.values" :multiple="true" v-else-if="attribute.typeSelection === 'TIPO_SELECAO_COMBO'" selectLabel="Clique" selectedLabel="Selecionado" placeholder="Selecione a opção">
      </multiselect>
      <input class="form-input" type="text" v-model="attribute.value" :disabled="disabledInputs()" v-else>
    </form-group>
  </shared-product-provide>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import SharedProductProvide from '@/components/marketplace/shared/provide/Product'
import FormGroup from '@/components/template/FormGroup'
import api from '@/api'
import Multiselect from 'vue-multiselect'
import Loader from '@/components/template/Loader'
import { isArray } from 'lodash'
import notifier from '@/notifier'
import { findCharacteristicValue, getProductCharacteristics } from '@/components/marketplace/shared/provide/presetCharacteristics'

export default {
  provide: {
    moduleDI: {
      type: 'marketplace',
      identifier: 'netshoes'
    },
    maxDescriptionLengthDI: 1230,
    allowDescriptionHtml: false
  },
  data: () => ({
    loadingProductTypes: false,
    loadingAttributes: false,
    brands: [],
    departments: [],
    productTypes: [],
    attributes: [],
    brand: null,
    department: null,
    businessId: null,
    productType: null,
    categoryPreset: null,
    disabledFormInput: true
  }),
  props: {
    product: {
      type: Object,
      required: true
    },
    fromModule: {
      type: Object,
      required: true
    },
    toModule: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapGetters('errors', [
      'getError'
    ])
  },
  async mounted () {
    this.loadingProductTypes = true

    try {
      this.brands = await api.netshoes.getBrands()
      this.departments = await api.netshoes.getProductTemplateDepartments()
    } catch (e) {
      this.brands = []
      this.departments = []
    }

    this.product.department = ''

    const presetCategorization = this.$parent.getPresetItemsCategorization(this.product.categories)
    if (presetCategorization !== null && presetCategorization !== undefined) {
      this.categoryPreset = presetCategorization.productType

      this.department = this.departments.find(channel => channel.businessId === presetCategorization.businessId)
        .departments.find(department => department.label === presetCategorization.department)
    }

    const presetBrand = this.$parent.getPresetItemByTypeAndFrom('brand', { brand: this.product.brand })

    if (presetBrand !== null && presetBrand !== undefined) {
      this.product.brand = presetBrand.brand
    } else if (this.brands.indexOf(this.product.brand) === -1) {
      this.brand = this.brands.find(brand => brand.toLowerCase() === this.product.brand.toLowerCase())

      if (this.brand !== undefined) {
        this.product.brand = this.brand
      } else {
        this.product.brand = ''
      }
    }

    this.loadingProductTypes = false
    this.$parent.loadingProducts(false)
  },
  methods: {
    ...mapActions('errors', [
      'addError',
      'clearErrors'
    ]),
    formatProduct () {
      this.clearErrors()

      this.attributes
        .map(attribute => {
          if (attribute.value === undefined && attribute.required) {
            this.addError({
              productId: this.product.productId,
              property: attribute.code,
              error: {
                message: 'required',
                value: 1
              }
            })
          }
        })

      const attributes = this.attributes
        .filter(attribute => {
          if (isArray(attribute.value)) {
            return attribute.value.length
          }

          return attribute.value !== undefined && attribute.value.trim() !== ''
        })
        .map(attribute => {
          return {
            id: attribute.name,
            label: attribute.name,
            valueId: attribute.value !== undefined && !isArray(attribute.value) ? [attribute.value] : attribute.value,
            valueLabel: attribute.value !== undefined && !isArray(attribute.value) ? [attribute.value].join((', ')) : attribute.value.join((', '))
          }
        })

      return this.product.variations
        .filter(variation => variation.selected)
        .map(variation => ({
          attributes: attributes,
          brand: this.product.brand,
          categories: this.product.categories,
          color: variation.attributes['color'] !== undefined ? variation.attributes['color'] : 'Única',
          department: this.product.department,
          description: this.product.description,
          ean: variation.ean,
          gender: variation.gender,
          grid: this.getGrid(variation),
          gridType: this.getType(variation),
          height: variation.height,
          images: variation.images.length ? variation.images.map(image => ({
            url: image
          })) : null,
          length: variation.length,
          original: {
            product: this.product,
            variation: variation
          },
          price: variation.price,
          productId: this.product.productId,
          productType: this.product.productType,
          sku: variation.sku,
          source: this.fromModule,
          stock: variation.stock,
          title: this.product.title,
          weight: variation.weight,
          width: variation.width
        })
      )
    },
    getType (variation) {
      if (variation.attributes['flavor']) {
        return 'flavor'
      }

      return 'size'
    },
    getGrid (variation) {
      if (variation.attributes['flavor']) {
        return variation.attributes['flavor']
      }

      if (variation.attributes['size']) {
        return variation.attributes['size']
      }

      return 'Único'
    },
    presetAttribute (attribute, characteristics) {
      if (attribute.value !== undefined) {
        return attribute
      }

      const value = findCharacteristicValue(attribute.name, attribute.values, characteristics)

      if (value !== undefined) {
        if (attribute.typeSelection === 'TIPO_SELECAO_LISTA') {
          attribute.value = value
        } else if (attribute.typeSelection === 'TIPO_SELECAO_COMBO') {
          attribute.value = [value]
        }
      }

      return attribute
    },
    disabledInputs () {
      return this.product.status === 'sent' || this.product.status === 'error'
    },
    getProductTemplateProductTypes (categoryPreset) {
      this.loadingProductTypes = true
      this.productType = null

      if (!this.product.department || !this.businessId) {
        this.attributes = []
        this.productTypes = []
        this.loadingProductTypes = false
        return false
      }

      api.netshoes.getProductTemplateProductTypes(this.businessId, this.product.department.replace(/ /g, '+'))
        .then(productTypes => {
          this.productTypes = productTypes

          if (categoryPreset !== null) {
            const index = productTypes.indexOf(categoryPreset)
            if (index !== -1) {
              this.productType = this.productTypes[index]
            }
          }

          this.loadingProductTypes = false
        })
        .catch(() => {
          notifier.error('Erro ao buscar tipos de produtos da Netshoes.', 'Erro')
          this.loadingProductTypes = false
        })
    },
    async getAttributes () {
      this.loadingAttributes = true
      try {
        this.attributes = (await api.netshoes.getAttributes(this.businessId, this.product.department.replace(/ /g, '+'), this.product.productType.replace(/ /g, '+')))
          .filter(attribute => ((attribute.typeSelection === 'TIPO_SELECAO_COMBO' || attribute.typeSelection === 'TIPO_SELECAO_LISTA') && attribute.values) || attribute.typeSelection === 'TIPO_SELECAO_TEXTO')
          .map((attribute) => {
            if (attribute.values !== undefined) {
              attribute.values.map(value => (value = value.value))
              for (var i in attribute.values) {
                attribute.values[i] = attribute.values[i].value
              }
            }

            return this.presetAttribute(attribute, getProductCharacteristics(this.product))
          })
        this.loadingAttributes = false
      } catch (error) {
        notifier.error('Atributos não encontrados para a categoria.', 'Erro')
        this.loadingAttributes = false
      }
    }
  },
  watch: {
    department (department) {
      this.attributes = []
      this.product.department = department.label
      this.businessId = department.businessId

      this.getProductTemplateProductTypes(this.categoryPreset)
    },
    productType (value) {
      this.product.productType = value

      if (!value) {
        this.attributes = []
        return
      }

      this.getAttributes()
    }
  },
  components: {
    SharedProductProvide,
    FormGroup,
    Multiselect,
    Loader
  }
}
</script>



// WEBPACK FOOTER //
// Product.vue?d4f4c236